import { useMemo, useCallback, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Navigation, Share2, Car, Footprints, Shuffle, MapPin, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import type { RouteResponse, CostingModel } from '@/api/geo';
import type { Waypoint } from '../lib/types';
import { generateShareUrl, isUrlTooLong } from '../lib/route-url';
import { cn } from '@/lib/utils';

interface RouteSummaryProps {
  routeResponse: RouteResponse | null;
  waypoints: Waypoint[];
  costing: CostingModel;
  isLoading: boolean;
  isOptimizing: boolean;
  onSetCosting: (costing: CostingModel) => void;
  onOptimize: () => void;
  onOptimizeFromStart: () => void;
}

function formatDistance(km: number): string {
  if (km < 1) {
    return `${Math.round(km * 1000)} м`;
  }
  return `${km.toFixed(1)} км`;
}

function formatDuration(seconds: number): string {
  const hours = Math.floor(seconds / 3600);
  const minutes = Math.round((seconds % 3600) / 60);

  if (hours > 0) {
    return `${hours} ч ${minutes} мин`;
  }
  return `${minutes} мин`;
}

export function RouteSummary({
  routeResponse,
  waypoints,
  costing,
  isLoading,
  isOptimizing,
  onSetCosting,
  onOptimize,
  onOptimizeFromStart,
}: RouteSummaryProps) {
  const navigate = useNavigate();
  const [shareLoading, setShareLoading] = useState(false);

  const summary = useMemo(() => {
    if (!routeResponse) return null;

    return {
      distance: routeResponse.trip.summary.length,
      duration: routeResponse.trip.summary.time,
    };
  }, [routeResponse]);

  const handleStartNavigation = useCallback(() => {
    if (waypoints.length < 2) return;

    // Navigate to first destination
    const destination = waypoints[1]; // Skip first waypoint (start point)
    navigate(`/navigation?lat=${destination.lat}&lon=${destination.lon}&mode=${costing}`);
  }, [waypoints, costing, navigate]);

  const handleShare = useCallback(async () => {
    if (waypoints.length === 0) return;

    setShareLoading(true);

    try {
      const url = generateShareUrl(waypoints, costing);

      if (isUrlTooLong(url)) {
        toast.error('Слишком много точек', {
          description: 'URL слишком длинный для шаринга. Уберите несколько точек.',
        });
        return;
      }

      if (navigator.share) {
        await navigator.share({
          title: 'Маршрут',
          text: `Маршрут из ${waypoints.length} точек`,
          url,
        });
      } else if (navigator.clipboard) {
        await navigator.clipboard.writeText(url);
        toast.success('Ссылка скопирована');
      }
    } catch (err) {
      // User cancelled share or error
      if ((err as Error).name !== 'AbortError') {
        console.error('Share failed:', err);
        toast.error('Не удалось поделиться');
      }
    } finally {
      setShareLoading(false);
    }
  }, [waypoints, costing]);

  const canOptimize = waypoints.length >= 3 && !isLoading && !isOptimizing;
  const canNavigate = waypoints.length >= 2 && summary && !isLoading;

  return (
    <div className="space-y-3">
      {/* Mode selector */}
      <div className="flex items-center gap-2">
        <Button
          variant={costing === 'auto' ? 'default' : 'outline'}
          size="sm"
          className="flex-1"
          onClick={() => onSetCosting('auto')}
          disabled={isLoading}
        >
          <Car className="w-4 h-4 mr-1" />
          Авто
        </Button>
        <Button
          variant={costing === 'pedestrian' ? 'default' : 'outline'}
          size="sm"
          className="flex-1"
          onClick={() => onSetCosting('pedestrian')}
          disabled={isLoading}
        >
          <Footprints className="w-4 h-4 mr-1" />
          Пешком
        </Button>
      </div>

      {/* Optimize buttons */}
      <div className="flex items-center gap-2">
        <Button
          variant="outline"
          size="sm"
          className="flex-1"
          onClick={onOptimizeFromStart}
          disabled={!canOptimize}
        >
          {isOptimizing ? (
            <Loader2 className="w-4 h-4 mr-1 animate-spin" />
          ) : (
            <MapPin className="w-4 h-4 mr-1" />
          )}
          <span className="text-xs">От старта</span>
        </Button>
        <Button
          variant="outline"
          size="sm"
          className="flex-1"
          onClick={onOptimize}
          disabled={!canOptimize}
        >
          {isOptimizing ? (
            <Loader2 className="w-4 h-4 mr-1 animate-spin" />
          ) : (
            <Shuffle className="w-4 h-4 mr-1" />
          )}
          <span className="text-xs">Кратчайший</span>
        </Button>
      </div>

      {/* Route summary */}
      {summary && (
        <div className="flex items-center justify-center gap-4 py-2 bg-muted rounded-lg">
          <div className="text-center">
            <div className="text-lg font-semibold">{formatDistance(summary.distance)}</div>
            <div className="text-xs text-muted-foreground">расстояние</div>
          </div>
          <div className="w-px h-8 bg-border" />
          <div className="text-center">
            <div className="text-lg font-semibold">{formatDuration(summary.duration)}</div>
            <div className="text-xs text-muted-foreground">время</div>
          </div>
        </div>
      )}

      {isLoading && !summary && (
        <div className="flex items-center justify-center gap-2 py-4 text-muted-foreground">
          <Loader2 className="w-4 h-4 animate-spin" />
          <span className="text-sm">Расчёт маршрута...</span>
        </div>
      )}

      {/* Action buttons */}
      <div className="flex items-center gap-2">
        <Button
          className={cn('flex-1', !canNavigate && 'opacity-50')}
          onClick={handleStartNavigation}
          disabled={!canNavigate}
        >
          <Navigation className="w-4 h-4 mr-2" />
          Начать
        </Button>
        <Button
          variant="outline"
          size="icon"
          onClick={handleShare}
          disabled={waypoints.length === 0 || shareLoading}
        >
          {shareLoading ? (
            <Loader2 className="w-4 h-4 animate-spin" />
          ) : (
            <Share2 className="w-4 h-4" />
          )}
        </Button>
      </div>
    </div>
  );
}
